import React, { useState, useRef, useEffect } from 'react';
import { TextInput, FormControl } from '@primer/react';

interface CodeInputProps {
    onComplete: (code: string) => void;
    onChange?: (code: string) => void;
    length?: number;
    placeholder?: string;
    disabled?: boolean;
    error?: boolean;
    autoFocus?: boolean;
}

export const CodeInput: React.FC<CodeInputProps> = ({
    onComplete,
    onChange,
    length = 6,
    placeholder = '',
    disabled = false,
    error = false,
    autoFocus = true
}) => {
    const [values, setValues] = useState<string[]>(new Array(length).fill(''));
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        if (autoFocus && inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, [autoFocus]);

    const handleChange = (index: number, value: string) => {
        const numericValue = value.replace(/[^0-9]/g, '');

        if (numericValue.length <= 1) {
            const newValues = [...values];
            newValues[index] = numericValue;
            setValues(newValues);

            const code = newValues.join('');
            onChange?.(code);

            if (numericValue && index < length - 1) {
                inputRefs.current[index + 1]?.focus();
            }

            if (code.length === length && !code.includes('')) {
                onComplete(code);
            }
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            const newValues = [...values];

            if (values[index]) {
                newValues[index] = '';
                setValues(newValues);
            } else if (index > 0) {
                newValues[index - 1] = '';
                setValues(newValues);
                inputRefs.current[index - 1]?.focus();
            }

            const code = newValues.join('');
            onChange?.(code);
        } else if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text').replace(/[^0-9]/g, '');

        if (pastedText.length >= length) {
            const newValues = pastedText.slice(0, length).split('');
            setValues(newValues);

            const code = newValues.join('');
            onChange?.(code);

            const lastIndex = Math.min(pastedText.length - 1, length - 1);
            inputRefs.current[lastIndex]?.focus();

            if (code.length === length) {
                onComplete(code);
            }
        }
    };

    const handleFocus = (index: number) => {
        inputRefs.current[index]?.select();
    };

    return (
        <div className="flex justify-between">
            {values.map((value, index) => (
                <FormControl required key={index} >
                    <FormControl.Label className='invisible h-0'></FormControl.Label>
                    <TextInput
                        ref={(el) => { inputRefs.current[index] = el; }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={value}
                        placeholder={placeholder}
                        disabled={disabled}
                        size="large"
                        contrast={!!(value && value.trim())}
                        className='w-[39.67px] h-[40px]'
                        onChange={(e) => handleChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        onFocus={() => handleFocus(index)}
                    />
                    {error && (
                        <FormControl.Validation variant="error" className='invisible h-0'></FormControl.Validation>
                    )}
                </FormControl>
            ))}
        </div>
    );
};

export default CodeInput;